/**
 * Date Filter Component
 * Calendar-style date picker for selecting data date
 */

import React, { useState, useRef, useEffect } from 'react';
import { useDateFilter } from '../contexts/DateFilterContext';

interface DateFilterProps {
  className?: string;
  showLabel?: boolean;
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const DateFilter: React.FC<DateFilterProps> = ({ className = '', showLabel = true }) => {
  const { selectedDate, setSelectedDate, minDate, maxDate, formatDate } = useDateFilter();
  const [isOpen, setIsOpen] = useState(false);
  const [viewMonth, setViewMonth] = useState(selectedDate.getMonth());
  const [viewYear, setViewYear] = useState(selectedDate.getFullYear());
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Update view when selected date changes
  useEffect(() => {
    setViewMonth(selectedDate.getMonth());
    setViewYear(selectedDate.getFullYear());
  }, [selectedDate]);

  const getDaysInMonth = (month: number, year: number): number => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (month: number, year: number): number => {
    return new Date(year, month, 1).getDay();
  };

  const isDateInRange = (date: Date): boolean => {
    return date >= minDate && date <= maxDate;
  };

  const isDateSelected = (day: number): boolean => {
    return (
      selectedDate.getDate() === day &&
      selectedDate.getMonth() === viewMonth &&
      selectedDate.getFullYear() === viewYear
    );
  };

  const handleDateSelect = (day: number) => {
    const newDate = new Date(viewYear, viewMonth, day);
    if (isDateInRange(newDate)) {
      setSelectedDate(newDate);
      setIsOpen(false);
    }
  };

  const canGoToPrevMonth = (): boolean => {
    const prevMonth = new Date(viewYear, viewMonth - 1, 1);
    return prevMonth >= new Date(minDate.getFullYear(), minDate.getMonth(), 1);
  };

  const canGoToNextMonth = (): boolean => {
    const nextMonth = new Date(viewYear, viewMonth + 1, 1);
    return nextMonth <= new Date(maxDate.getFullYear(), maxDate.getMonth(), 1);
  };

  const handlePrevMonth = () => {
    if (canGoToPrevMonth()) {
      if (viewMonth === 0) {
        setViewMonth(11);
        setViewYear(viewYear - 1);
      } else {
        setViewMonth(viewMonth - 1);
      }
    }
  };

  const handleNextMonth = () => {
    if (canGoToNextMonth()) {
      if (viewMonth === 11) {
        setViewMonth(0);
        setViewYear(viewYear + 1);
      } else {
        setViewMonth(viewMonth + 1);
      }
    }
  };

  const renderCalendarDays = () => {
    const daysInMonth = getDaysInMonth(viewMonth, viewYear);
    const firstDay = getFirstDayOfMonth(viewMonth, viewYear);
    const days: React.ReactNode[] = [];

    // Empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(
        <div key={`empty-${i}`} className="h-8 w-8" />
      );
    }

    // Actual days
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(viewYear, viewMonth, day);
      const inRange = isDateInRange(date);
      const selected = isDateSelected(day);
      const isToday = new Date().toDateString() === date.toDateString();

      days.push(
        <button
          key={day}
          onClick={() => handleDateSelect(day)}
          disabled={!inRange}
          className={`h-8 w-8 rounded-full text-sm font-medium transition-all ${
            selected
              ? 'bg-primary text-white'
              : inRange
              ? 'hover:bg-primary/20 text-foreground'
              : 'text-text-muted/30 cursor-not-allowed'
          } ${isToday && !selected ? 'ring-1 ring-primary' : ''}`}
        >
          {day}
        </button>
      );
    }

    return days;
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 bg-background-secondary text-foreground rounded-lg px-3 py-2 border border-border hover:border-primary focus:outline-none focus:ring-2 focus:ring-primary text-sm transition-all"
      >
        <i className="fas fa-calendar-alt text-primary"></i>
        {showLabel && <span className="text-text-muted">Data:</span>}
        <span className="font-medium">{formatDate(selectedDate)}</span>
        <i className={`fas fa-chevron-down text-xs text-text-muted transition-transform ${isOpen ? 'rotate-180' : ''}`}></i>
      </button>

      {/* Calendar Dropdown */}
      {isOpen && (
        <div className="absolute top-full right-0 mt-2 bg-card border border-border rounded-xl shadow-xl z-50 p-4 min-w-[300px]">
          {/* Month/Year Header */}
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={handlePrevMonth}
              disabled={!canGoToPrevMonth()}
              className={`p-1.5 rounded-lg transition-colors ${
                canGoToPrevMonth()
                  ? 'hover:bg-background-secondary text-foreground'
                  : 'text-text-muted/30 cursor-not-allowed'
              }`}
            >
              <i className="fas fa-chevron-left text-sm"></i>
            </button>
            <span className="text-lg font-semibold text-foreground">
              {MONTH_NAMES[viewMonth]} {viewYear}
            </span>
            <button
              onClick={handleNextMonth}
              disabled={!canGoToNextMonth()}
              className={`p-1.5 rounded-lg transition-colors ${
                canGoToNextMonth()
                  ? 'hover:bg-background-secondary text-foreground'
                  : 'text-text-muted/30 cursor-not-allowed'
              }`}
            >
              <i className="fas fa-chevron-right text-sm"></i>
            </button>
          </div>

          {/* Day Names */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {DAY_NAMES.map(day => (
              <div key={day} className="h-8 w-8 flex items-center justify-center text-xs text-text-muted font-medium">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-1">
            {renderCalendarDays()}
          </div>

          {/* Data Range Info */}
          <div className="mt-4 pt-3 border-t border-border">
            <p className="text-xs text-text-muted text-center">
              Data available: June 1, 2025 - December 31, 2025
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateFilter;
